import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL:         BASE_URL,
  timeout:         12000,   // 12s — reduced from 30s; shows error faster if server down
  headers:         { 'Content-Type': 'application/json' },
  withCredentials: false,
});

// ── REQUEST: Attach JWT ──────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('faristaa_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Tag with retry count
    config._retryCount = config._retryCount || 0;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── RESPONSE: Smart error handling + auto-retry ──────────────────────────
api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const config  = error.config;
    const status  = error.response?.status;
    const errData = error.response?.data || {};
    // Friendly error messages for common failure modes
    let errMsg = errData.error || errData.message || error.message || 'Something went wrong';
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      errMsg = 'Server is taking too long to respond. Please check your internet connection and try again.';
    } else if (!error.response && error.code !== 'ERR_CANCELED') {
      errMsg = 'Cannot reach the server. Please check your internet connection.';
    }

    // Auto-retry on network error or 5xx (max 2 retries, NOT on 4xx)
    const isNetworkError = !error.response && error.code !== 'ECONNABORTED';
    const isServerError  = status >= 500;
    const shouldRetry    = (isNetworkError || isServerError) && config && config._retryCount < 2;

    if (shouldRetry) {
      config._retryCount += 1;
      const delay = config._retryCount * 1500; // 1.5s, 3s
      await new Promise(r => setTimeout(r, delay));
      return api(config);
    }

    // 401 — only auto-logout for token errors
    if (status === 401) {
      const msg          = errMsg.toLowerCase();
      const isTokenError = (
        errData.isTokenError === true ||
        msg.includes('not authenticated') ||
        msg.includes('jwt expired') ||
        msg.includes('jwt malformed') ||
        msg.includes('token expired') ||
        msg.includes('invalid or expired token') ||
        msg.includes('no token provided')
      );
      const path       = window.location.pathname;
      const isAuthPage = path.includes('/login') || path.includes('/register') ||
                         path.includes('/setup-profile') || path.includes('/forgot');
      if (isTokenError && !isAuthPage) {
        clearAuthData();
        sessionStorage.removeItem('loginOrigin');
        sessionStorage.removeItem('pendingBooking');
        window.dispatchEvent(new Event('auth:logout'));
      }
    }

    return Promise.reject(Object.assign(new Error(errMsg), { status, response: error.response }));
  }
);

export default api;

// ── Auth storage ──────────────────────────────────────────────────────────
export const setAuthData = (token, user, role) => {
  try {
    localStorage.setItem('faristaa_token', token);
    localStorage.setItem('faristaa_user', JSON.stringify(user));
    localStorage.setItem('faristaa_role', role);
  } catch {} // storage quota exceeded — silent
};

export const clearAuthData = () => {
  localStorage.removeItem('faristaa_token');
  localStorage.removeItem('faristaa_user');
  localStorage.removeItem('faristaa_role');
};

export const getStoredUser  = () => {
  try { return JSON.parse(localStorage.getItem('faristaa_user')); } catch { return null; }
};
export const getStoredRole  = () => localStorage.getItem('faristaa_role');
export const getStoredToken = () => localStorage.getItem('faristaa_token');

// ── Network status helper ─────────────────────────────────────────────────
export const isOnline = () => navigator.onLine;
